import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

import { StatsCards, type FilterType } from '@/components/StatsCards';
import { AmountSummary, type AmountFilter } from '@/components/AmountSummary';
import { FacturasTable } from '@/components/FacturasTable';
import { FilterBar } from '@/components/FilterBar';
import { UploadModal } from '@/components/UploadModal';
import { GmailSyncModal } from '@/components/GmailSyncModal';
import { UserMenu } from '@/components/UserMenu';
import { TipoFacturaSelector, compraItems } from '@/components/TipoFacturaSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FileText, Upload, Search, Download, Mail, AlertTriangle, XCircle, RefreshCw, CheckCircle, Flag, FlagOff } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function Dashboard() {
  const { usuario } = useAuth();
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [statsFilter, setStatsFilter] = useState<FilterType>('all');
  const [amountFilter, setAmountFilter] = useState<AmountFilter>(null);
  const [gmailSyncOpen, setGmailSyncOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // ERP Sync state
  const [erpSyncOpen, setErpSyncOpen] = useState(false);
  const [erpSyncStep, setErpSyncStep] = useState<'input' | 'syncing' | 'done'>('input');
  const [erpMode, setErpMode] = useState<'sync' | 'unsync'>('sync');
  const [erpInputText, setErpInputText] = useState('');
  const [erpSyncResults, setErpSyncResults] = useState<Array<{ numero: string; status: 'ok' | 'skipped' | 'error'; message: string }>>([]);

  // Filtros para los montos (excluir page/limit que no aplican a stats)
  const statsParams = Object.fromEntries(
    Object.entries({ ...filters, search }).filter(([k, v]) => v && k !== 'page' && k !== 'limit')
  );

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['stats', statsParams],
    queryFn: () => api.getStats(statsParams),
  });

  const { data: robotLinkData } = useQuery({
    queryKey: ['robot-link'],
    queryFn: () => api.getRobotLink(),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
  const robotLink = robotLinkData?.link || '';

  const handleActivarRobot = () => {
    if (!robotLink) {
      alert('No hay link configurado para el robot. Configurelo via POST /api/robot-link');
      return;
    }
    window.open(robotLink, '_blank', 'noopener,noreferrer');
  };

  const { data: facturasData, isLoading: facturasLoading } = useQuery({
    queryKey: ['facturas', filters, search, page, limit, sortBy, sortDir],
    queryFn: () => api.getFacturas({
      ...filters, search, page: String(page),
      ...(limit > 0 ? { limit: String(limit) } : { limit: '10000' }),
      ...(sortBy ? { sort_by: sortBy, sort_dir: sortDir } : {}),
    }),
  });

  const handleUploadSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['facturas'] });
    queryClient.invalidateQueries({ queryKey: ['stats'] });
    setUploadOpen(false);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['facturas'] });
    queryClient.invalidateQueries({ queryKey: ['stats'] });
  };

  const [isExporting, setIsExporting] = useState(false);
  const [exportValidation, setExportValidation] = useState<{ open: boolean; issues: Array<{ numero: string; missing: string[] }> }>({ open: false, issues: [] });
  const [missingCodesModal, setMissingCodesModal] = useState<{
    open: boolean;
    items: Array<{ factura: string; supplier_code: string; sellers_item_id: string; description: string; internal_code: string }>;
    pendingExportIds: string[];
  }>({ open: false, items: [], pendingExportIds: [] });
  const [isSavingCodes, setIsSavingCodes] = useState(false);

  const handleExportExcel = async () => {
    let facturas: Array<{ id: string; numero: string; tipo_factura: string | null; tipo_iva: string | null; subtipo_factura: string | null }>;

    if (selectedIds.size > 0) {
      // Exportar solo las seleccionadas manualmente (de la pagina actual)
      const allFacturas = (facturasData?.data || []).filter((f: any) => !f.erp);
      facturas = allFacturas.filter((f: any) => selectedIds.has(f.id));
    } else {
      // Exportar TODAS las facturas que coinciden con los filtros actuales
      const exportIdsData = await api.getExportIds({ ...filters, search });
      facturas = exportIdsData.facturas;
    }

    if (facturas.length === 0) return;

    // Fase 1: Validar campos basicos antes de llamar al API
    const preIssues: Array<{ numero: string; missing: string[] }> = [];
    for (const f of facturas) {
      const missing: string[] = [];
      if (!f.tipo_factura) missing.push('Tipo de Factura');
      if (!f.tipo_iva) missing.push('Tipo de IVA');
      if (!f.subtipo_factura) missing.push('Subtipo / Codigo Contable');
      if (missing.length > 0) preIssues.push({ numero: f.numero, missing });
    }
    if (preIssues.length > 0) {
      setExportValidation({ open: true, issues: preIssues });
      return;
    }

    setIsExporting(true);
    try {
      // Obtener facturas con items del XML desde el API
      const ids = facturas.map((f: any) => f.id);

      // Hacer batch requests si hay mas de 500 IDs (limite del backend)
      const BATCH_SIZE = 500;
      let allExportData: any[] = [];
      for (let i = 0; i < ids.length; i += BATCH_SIZE) {
        const batch = ids.slice(i, i + BATCH_SIZE);
        const batchResult = await api.exportFacturas(batch);
        allExportData = allExportData.concat(batchResult.data);
      }
      const exportData = { data: allExportData };

      // Fase 2: Validar catalogo proveedor, codigo interno (compras) y subtipo per-linea (gastos)
      const itemIssues: Array<{ numero: string; missing: string[] }> = [];
      const missingCodeItems: Array<{ factura: string; supplier_code: string; sellers_item_id: string; description: string; internal_code: string }> = [];
      let hasMissingCodes = false;
      for (const f of exportData.data as any[]) {
        const missing: string[] = [];
        // Validar datos del catalogo de proveedores
        if (!f.codigo_cta) missing.push('Codigo cuenta (CTOCTA) - catalogo proveedor');
        // Validar items
        const esCompra = f.tipo_factura === 'compra' || (f.tipo_factura === 'nota_credito' && f.subtipo_factura === 'mercancia_confirmada');
        if (esCompra && f.items.length > 0) {
          const sinCodigo = f.items.filter((i: any) => !i.internal_code);
          if (sinCodigo.length > 0) {
            hasMissingCodes = true;
            // Recopilar items sin codigo para el modal interactivo
            for (const item of sinCodigo) {
              const key = `${f.supplier_code}::${item.sellers_item_id}`;
              if (!missingCodeItems.some(m => `${m.supplier_code}::${m.sellers_item_id}` === key)) {
                missingCodeItems.push({
                  factura: f.numero,
                  supplier_code: f.supplier_code || '',
                  sellers_item_id: item.sellers_item_id || '',
                  description: item.description || '',
                  internal_code: '',
                });
              }
            }
          }
        } else if (f.items.length > 1) {
          // Gasto con multiples items: cada linea necesita subtipo asignado
          const sinSubtipo = f.items.filter((i: any) => !i.subtipo_factura);
          if (sinSubtipo.length > 0) {
            missing.push(`Codigo contable faltante en ${sinSubtipo.length} linea(s)`);
          }
        }
        if (missing.length > 0) itemIssues.push({ numero: f.numero, missing });
      }
      // Si hay otros errores (no codigo interno), mostrar el dialog de error normal
      if (itemIssues.length > 0) {
        setExportValidation({ open: true, issues: itemIssues });
        return;
      }
      // Si solo faltan codigos internos, mostrar modal interactivo
      if (hasMissingCodes) {
        setMissingCodesModal({ open: true, items: missingCodeItems, pendingExportIds: ids });
        return;
      }

      // Mapeo tipo_factura -> codigo documento DIAN
      const tipoFacturaCodigo: Record<string, string> = {
        gasto: 'Fi',
        compra: 'F2',
        nota_credito: 'NK',
      };


      const rows: Record<string, any>[] = [];

      const columnOrder = [
        'DCTOPRV', 'Proveedor', 'NIT', 'CTOCTA', 'Cuenta x Pagar',
        'Cod Retencion', 'RTEFTE', 'Cod Rete', 'Top Rete', 'PO Rete', 'Cod CC',
        'Receptor', 'NIT Receptor', 'TIPODCTO', 'TIPOMVTO', 'Subtipo',
        'Cod Interno', 'PRODUCTOS', 'NOMBRE', 'UNDBASE', 'VALORUNIT',
        'Cantidad', 'Tipo IVA', 'TAX', 'FECHA', 'FECHA1',
        'Subtotal', 'Vr IVA', 'Valor Total', 'VLRLTFTE', 'Estado',
      ];

      function makeRow(fields: Record<string, any>): Record<string, any> {
        const ordered: Record<string, any> = {};
        for (const col of columnOrder) ordered[col] = fields[col] ?? '';
        return ordered;
      }

      function parseTax(val: string | number | null | undefined): number {
        if (val == null) return 0;
        const n = typeof val === 'number' ? val : parseInt(val, 10);
        return isNaN(n) ? 0 : n;
      }

      function isIncItem(item: { tipo_iva?: string }): boolean {
        return item.tipo_iva === 's51152001' || item.tipo_iva === 's52152001';
      }

      // Calcular VLRLTFTE: subtotalLinea * (rtefte / 100), con filtros de top_rete
      function calcVlrltfte(subtotalLinea: number, rtefte: number | null, topRete: number | null, subtotalFactura: number): number {
        const r = rtefte ?? 0;
        if (r === 0) return 0;
        const t = topRete ?? 0;
        if (t === 0 || t === 1) return Math.round(subtotalLinea * r / 100);
        // top_rete es un umbral: aplicar solo si subtotal de factura >= umbral
        if (subtotalFactura >= t) return Math.round(subtotalLinea * r / 100);
        return 0;
      }

      for (const f of exportData.data) {
        const common: Record<string, any> = {
          'DCTOPRV': f.numero,
          'Proveedor': f.proveedor_razon_social || '',
          'NIT': f.proveedor_nit || '',
          'CTOCTA': f.codigo_cta || '',
          'Cuenta x Pagar': f.cuenta_x_pagar || '',
          'Cod Retencion': f.cod_retencion || '',
          'RTEFTE': f.rtefte ?? '',
          'Cod Rete': f.cod_rete || '',
          'Top Rete': f.top_rete ?? '',
          'PO Rete': f.po_rete ?? '',
          'Cod CC': f.cod_cc || '',
          'Receptor': f.receptor_nombre || '',
          'NIT Receptor': f.receptor_nit || '',
          'TIPODCTO': tipoFacturaCodigo[f.tipo_factura || ''] || f.tipo_factura || '',
          'TIPOMVTO': f.tipo_factura === 'nota_credito' ? '06' : '01',
          'Tipo IVA': f.tipo_iva || '',
          'TAX': parseTax(f.tax_percent),
          'FECHA': f.fecha_emision || '',
          'FECHA1': f.fecha_vencimiento || '',
          'Estado': f.estado,
        };

        // Helper: build retention override fields for a line item
        function retencionOverride(item: typeof f.items[0], subtotalLinea: number) {
          const ret = item.retencion_line;
          if (!ret) {
            return {
              'VLRLTFTE': calcVlrltfte(subtotalLinea, f.rtefte, f.top_rete, f.valor_subtotal),
            };
          }
          return {
            'Cod Retencion': ret.cod_retencion || '',
            'RTEFTE': ret.rtefte ?? '',
            'Cod Rete': ret.cod_rete || '',
            'Top Rete': ret.top_rete ?? '',
            'PO Rete': ret.po_rete ?? '',
            'VLRLTFTE': calcVlrltfte(subtotalLinea, ret.rtefte, ret.top_rete, f.valor_subtotal),
          };
        }

        // Facturas con productos: compra, o nota_credito de mercancia
        const esCompra = f.tipo_factura === 'compra' || (f.tipo_factura === 'nota_credito' && f.subtipo_factura === 'mercancia_confirmada');

        if (esCompra && f.items.length > 0) {
          for (const item of f.items) {
            const subtipo = item.internal_code || item.sellers_item_id || item.description || '';
            const itemIsInc = isIncItem(item);
            rows.push(makeRow({
              ...common,
              ...retencionOverride(item, item.subtotal),
              'Subtipo': subtipo,
              'Cod Interno': item.internal_code || '',
              'PRODUCTOS': item.internal_code || '',
              'NOMBRE': item.equivalency_description || item.description || '',
              'UNDBASE': 'UND',
              'VALORUNIT': item.price || '',
              'Cantidad': item.quantity || '',
              'Tipo IVA': itemIsInc ? '0' : (item.tipo_iva || f.tipo_iva || ''),
              'TAX': itemIsInc ? 0 : parseTax(item.tax_percent_line ?? f.tax_percent),
              'Subtotal': item.subtotal,
              'Vr IVA': itemIsInc ? 0 : item.tax_amount,
              'Valor Total': itemIsInc ? item.subtotal : item.total,
            }));
            if (itemIsInc) {
              rows.push(makeRow({
                ...common,
                'VLRLTFTE': 0,
                'Subtipo': item.tipo_iva,
                'Cod Interno': '',
                'PRODUCTOS': item.tipo_iva,
                'NOMBRE': item.equivalency_description || item.description || '',
                'UNDBASE': '0',
                'VALORUNIT': item.tax_amount,
                'Cantidad': 1,
                'Tipo IVA': '0',
                'TAX': 0,
                'Subtotal': item.tax_amount,
                'Vr IVA': 0,
                'Valor Total': item.tax_amount,
              }));
            }
          }
        } else if (f.items.length > 1) {
          // Gasto o nota_credito de gasto con multiples items/IVA: un renglon por cada item
          for (const item of f.items) {
            const codigoContable = item.subtipo_factura || f.subtipo_factura || '';
            const servicio = compraItems.find(ci => ci.codigo === codigoContable);
            const itemIsInc = isIncItem(item);
            rows.push(makeRow({
              ...common,
              ...retencionOverride(item, item.subtotal),
              'Subtipo': codigoContable,
              'PRODUCTOS': codigoContable,
              'NOMBRE': servicio?.nombre || item.description || '',
              'UNDBASE': '0',
              'VALORUNIT': item.price || '',
              'Cantidad': item.quantity || '',
              'Tipo IVA': itemIsInc ? '0' : (item.tipo_iva || f.tipo_iva || ''),
              'TAX': itemIsInc ? 0 : parseTax(item.tax_percent_line ?? f.tax_percent),
              'Subtotal': item.subtotal,
              'Vr IVA': itemIsInc ? 0 : item.tax_amount,
              'Valor Total': itemIsInc ? item.subtotal : item.total,
            }));
            if (itemIsInc) {
              rows.push(makeRow({
                ...common,
                'VLRLTFTE': 0,
                'Subtipo': item.tipo_iva,
                'Cod Interno': '',
                'PRODUCTOS': item.tipo_iva,
                'NOMBRE': servicio?.nombre || item.description || '',
                'UNDBASE': '0',
                'VALORUNIT': item.tax_amount,
                'Cantidad': 1,
                'Tipo IVA': '0',
                'TAX': 0,
                'Subtotal': item.tax_amount,
                'Vr IVA': 0,
                'Valor Total': item.tax_amount,
              }));
            }
          }
        } else {
          // Gasto o nota_credito de gasto con 0 o 1 item
          const codigoContable = f.subtipo_factura || '';
          const servicio = compraItems.find(ci => ci.codigo === codigoContable);
          const singleItem = f.items.length === 1 ? f.items[0] : null;
          const retOverride = singleItem ? retencionOverride(singleItem, f.valor_subtotal) : {
            'VLRLTFTE': calcVlrltfte(f.valor_subtotal, f.rtefte, f.top_rete, f.valor_subtotal),
          };
          const itemIsInc = singleItem && isIncItem(singleItem);
          rows.push(makeRow({
            ...common,
            ...retOverride,
            'Subtipo': codigoContable,
            'PRODUCTOS': codigoContable,
            'NOMBRE': servicio?.nombre || singleItem?.description || '',
            'UNDBASE': '0',
            'VALORUNIT': singleItem?.price || '',
            'Cantidad': singleItem?.quantity || '',
            'Tipo IVA': itemIsInc ? '0' : common['Tipo IVA'],
            'TAX': itemIsInc ? 0 : common['TAX'],
            'Subtotal': itemIsInc ? f.valor_subtotal : f.valor_subtotal,
            'Vr IVA': itemIsInc ? 0 : f.valor_iva,
            'Valor Total': itemIsInc ? f.valor_subtotal : f.valor_total,
          }));
          if (itemIsInc && singleItem) {
            rows.push(makeRow({
              ...common,
              'VLRLTFTE': 0,
              'Subtipo': singleItem.tipo_iva,
              'Cod Interno': '',
              'PRODUCTOS': singleItem.tipo_iva,
              'NOMBRE': servicio?.nombre || singleItem.description || '',
              'UNDBASE': '0',
              'VALORUNIT': singleItem.tax_amount,
              'Cantidad': 1,
              'Tipo IVA': '0',
              'TAX': 0,
              'Subtotal': singleItem.tax_amount,
              'Vr IVA': 0,
              'Valor Total': singleItem.tax_amount,
            }));
          }
        }
      }

      const ws = XLSX.utils.json_to_sheet(rows, { header: columnOrder });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Facturas');
      XLSX.writeFile(wb, 'FACTURAS.xlsx');
    } catch (err) {
      console.error('Error al exportar:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveCodesAndExport = async () => {
    const { items } = missingCodesModal;
    // Validar que todos los campos estén llenos
    const incomplete = items.filter(i => !i.internal_code.trim());
    if (incomplete.length > 0) return;

    setIsSavingCodes(true);
    try {
      // Agrupar por supplier_code
      const bySupplier = new Map<string, Array<{ external_code: string; internal_code: string; description?: string }>>();
      for (const item of items) {
        if (!bySupplier.has(item.supplier_code)) bySupplier.set(item.supplier_code, []);
        bySupplier.get(item.supplier_code)!.push({
          external_code: item.sellers_item_id,
          internal_code: item.internal_code.trim(),
          description: item.description || undefined,
        });
      }

      // Guardar equivalencias por cada proveedor
      for (const [supplierCode, equivItems] of bySupplier) {
        await api.importEquivalencias(supplierCode, 'products', equivItems);
      }

      // Cerrar modal y re-ejecutar la exportación (ahora los códigos ya existen)
      setMissingCodesModal({ open: false, items: [], pendingExportIds: [] });
      setIsSavingCodes(false);

      // Re-ejecutar handleExportExcel que ahora encontrará los códigos
      handleExportExcel();
    } catch (err) {
      console.error('Error al guardar codigos:', err);
      setIsSavingCodes(false);
    }
  };

  const handleErpSyncConfirm = async () => {
    // Parse input text into unique numbers
    const numbers = new Set<string>();
    for (const part of erpInputText.split(',')) {
      const trimmed = part.trim();
      if (trimmed) numbers.add(trimmed);
    }
    const parsed = Array.from(numbers);
    if (parsed.length === 0) return;
    setErpSyncStep('syncing');
    const results: Array<{ numero: string; status: 'ok' | 'skipped' | 'error'; message: string }> = [];

    // Check which are already synced from current facturas data
    const allFacturas = facturasData?.data || [];
    const erpMap = new Map(allFacturas.map((f: any) => [f.numero, f.erp]));

    for (const numero of parsed) {
      // Skip if already in desired state
      const isErp = !!erpMap.get(numero);
      if (erpMode === 'sync' && isErp) {
        results.push({ numero, status: 'skipped', message: 'Ya sincronizada' });
        continue;
      }
      if (erpMode === 'unsync' && !isErp) {
        results.push({ numero, status: 'skipped', message: 'Ya no esta sincronizada' });
        continue;
      }
      try {
        if (erpMode === 'sync') {
          await api.syncErp(numero);
          results.push({ numero, status: 'ok', message: 'Sincronizada' });
        } else {
          await api.unsyncErp(numero);
          results.push({ numero, status: 'ok', message: 'Desincronizada' });
        }
      } catch (err: any) {
        const msg = err?.body?.error || err?.message || 'Error';
        results.push({ numero, status: 'error', message: msg });
      }
    }

    setErpSyncResults(results);
    setErpSyncStep('done');
    queryClient.invalidateQueries({ queryKey: ['facturas'] });
  };

  const handleErpSyncClose = () => {
    setErpSyncOpen(false);
    setErpSyncStep('input');
    setErpInputText('');
    setErpSyncResults([]);
  };

  const handleFilterChange = (newFilters: Record<string, string>) => {
    // Si el usuario cambia filtros manualmente, limpiar selección de paneles
    if (newFilters.estado !== filters.estado) {
      setAmountFilter(null);
      setStatsFilter('all');
    }
    setFilters(newFilters);
    setPage(1);
  };

  const handleTipoFacturaChange = (tipoFactura: string | undefined) => {
    const newFilters = { ...filters };
    if (tipoFactura) {
      newFilters.tipo_factura = tipoFactura;
    } else {
      delete newFilters.tipo_factura;
    }
    setFilters(newFilters);
    setPage(1);
  };

  const handleStatsFilterChange = (filter: FilterType) => {
    setStatsFilter(filter);
    setAmountFilter(null); // Limpiar filtro de montos
    const newFilters = { ...filters };

    // Limpiar filtros de estado previos
    delete newFilters.estado;
    delete newFilters.es_critica;

    // Aplicar nuevo filtro según la selección
    if (filter !== 'all') {
      if (filter === 'criticas') {
        newFilters.es_critica = 'true';
      } else {
        newFilters.estado = filter;
      }
    }

    setFilters(newFilters);
    setPage(1);
  };

  const handleAmountFilterChange = (filter: AmountFilter) => {
    setAmountFilter(filter);
    setStatsFilter('all'); // Limpiar filtro de stats cards
    const newFilters = { ...filters };

    // Limpiar filtros de estado previos
    delete newFilters.estado;
    delete newFilters.es_critica;

    if (filter === 'pendiente') {
      newFilters.estado = 'pendiente,en_espera';
    } else if (filter === 'aprobado') {
      newFilters.estado = 'aprobada';
    }

    setFilters(newFilters);
    setPage(1);
  };

  const handleGmailSyncSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['facturas'] });
    queryClient.invalidateQueries({ queryKey: ['stats'] });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">Sistema de Aprobacion de Facturas</h1>
                <p className="text-sm text-muted-foreground">Gestion y validacion de documentos fiscales</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={handleActivarRobot}
                disabled={!robotLink}
                title={robotLink || 'Sin link configurado'}
                className="px-3 py-1.5 text-sm font-semibold rounded-md bg-yellow-400 text-black hover:bg-yellow-500 active:bg-yellow-600 disabled:bg-yellow-200 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
              >
                Activar Robot
              </button>
              {stats && (
                <div className="hidden md:flex items-center gap-2">
                  <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                    {stats.pendientes} pendientes
                  </span>
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                    {stats.criticas} criticas
                  </span>
                </div>
              )}
              <UserMenu usuario={usuario} />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-8">
        {/* Stats Section */}
        <div className="space-y-6 mb-8">
          <StatsCards
            stats={stats}
            isLoading={statsLoading}
            activeFilter={statsFilter}
            onFilterChange={handleStatsFilterChange}
          />
          <AmountSummary
            stats={stats}
            isLoading={statsLoading}
            activeFilter={amountFilter}
            onFilterClick={handleAmountFilterChange}
          />
        </div>

        {/* Facturas Section */}
        <div className="bg-white rounded-lg shadow">
          {/* Toolbar */}
          <div className="p-4 border-b space-y-3">
            {/* Row 1: Search + action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por numero o proveedor..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 text-foreground bg-white"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => setGmailSyncOpen(true)}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Sync Gmail
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setErpMode('sync'); setErpSyncOpen(true); }}
                >
                  <Flag className="h-4 w-4 mr-2" />
                  Sync ERP
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setErpMode('unsync'); setErpSyncOpen(true); }}
                >
                  <FlagOff className="h-4 w-4 mr-2" />
                  UnSync ERP
                </Button>
                <Button
                  variant="outline"
                  onClick={handleExportExcel}
                  disabled={!facturasData?.data?.length || selectedIds.size === 0 || isExporting}
                >
                  <Download className={`h-4 w-4 mr-2 ${isExporting ? 'animate-pulse' : ''}`} />
                  {isExporting ? 'Exportando...' : selectedIds.size > 0 ? `Exportar (${selectedIds.size})` : 'Exportar Excel'}
                </Button>
                <Button onClick={() => setUploadOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Cargar XML
                </Button>
              </div>
            </div>
            {/* Row 2: Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">Mostrar</span>
                <Select
                  value={String(limit)}
                  onValueChange={(val) => { setLimit(Number(val)); setPage(1); }}
                >
                  <SelectTrigger className="h-8 w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                    <SelectItem value="0">Todos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <TipoFacturaSelector value={filters.tipo_factura} onChange={handleTipoFacturaChange} />
              <FilterBar filters={filters} onChange={handleFilterChange} />
            </div>
          </div>

          {/* Table */}
          <FacturasTable
            facturas={facturasData?.data || []}
            isLoading={facturasLoading}
            page={page}
            totalPages={facturasData?.total_pages || 1}
            total={facturasData?.total || 0}
            onPageChange={setPage}
            limit={limit}
            onSelectionChange={setSelectedIds}
            onRefresh={handleRefresh}
            userRole={usuario?.rol}
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(newSortBy, newSortDir) => {
              setSortBy(newSortBy);
              setSortDir(newSortDir);
              setPage(1);
            }}
          />
        </div>
      </main>

      {/* Upload Modal */}
      <UploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onSuccess={handleUploadSuccess}
      />

      {/* Gmail Sync Modal */}
      <GmailSyncModal
        open={gmailSyncOpen}
        onOpenChange={setGmailSyncOpen}
        onSuccess={handleGmailSyncSuccess}
      />

      {/* Dialog de validación - Campos requeridos para exportar */}
      <Dialog open={exportValidation.open} onOpenChange={(open) => !open && setExportValidation({ open: false, issues: [] })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              No se puede exportar
            </DialogTitle>
            <DialogDescription asChild>
              <div>
                <p className="mb-3">Las siguientes facturas tienen campos obligatorios sin asignar:</p>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {exportValidation.issues.map((issue, i) => (
                    <div key={i} className="p-3 bg-muted rounded-md">
                      <p className="font-medium text-foreground mb-1">Factura: {issue.numero}</p>
                      {issue.missing.map((m, j) => (
                        <p key={j} className="text-sm flex items-center gap-2 text-red-600">
                          <XCircle className="h-4 w-4 flex-shrink-0" />
                          {m}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setExportValidation({ open: false, issues: [] })}>
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de códigos internos faltantes */}
      <Dialog open={missingCodesModal.open} onOpenChange={(open) => !open && setMissingCodesModal({ open: false, items: [], pendingExportIds: [] })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-yellow-600">
              <AlertTriangle className="h-5 w-5" />
              Codigos internos faltantes
            </DialogTitle>
            <DialogDescription>
              Los siguientes productos no tienen codigo interno asignado. Ingresa el codigo para continuar con la exportacion.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-3">
            {missingCodesModal.items.map((item, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_150px] gap-2 items-center p-2 bg-muted rounded-md">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Cod. Proveedor</p>
                  <p className="text-sm font-medium truncate">{item.sellers_item_id}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Descripcion</p>
                  <p className="text-sm truncate">{item.description}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Cod. Interno</p>
                  <Input
                    value={item.internal_code}
                    onChange={(e) => {
                      const updated = [...missingCodesModal.items];
                      updated[i] = { ...updated[i], internal_code: e.target.value };
                      setMissingCodesModal(prev => ({ ...prev, items: updated }));
                    }}
                    placeholder="Codigo"
                    className={`h-8 ${!item.internal_code.trim() ? 'border-red-300' : 'border-green-300'}`}
                  />
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMissingCodesModal({ open: false, items: [], pendingExportIds: [] })}>
              Cancelar
            </Button>
            <Button
              onClick={handleSaveCodesAndExport}
              disabled={isSavingCodes || missingCodesModal.items.some(i => !i.internal_code.trim())}
            >
              {isSavingCodes ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                <><CheckCircle className="h-4 w-4 mr-2" /> Guardar y Exportar</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Sync ERP */}
      <Dialog open={erpSyncOpen} onOpenChange={(open) => { if (!open) handleErpSyncClose(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {erpMode === 'sync' ? <Flag className="h-5 w-5" /> : <FlagOff className="h-5 w-5" />}
              {erpMode === 'sync' ? 'Sync ERP' : 'UnSync ERP'}
            </DialogTitle>
          </DialogHeader>

          {erpSyncStep === 'input' && (
            <div className="space-y-4">
              <DialogDescription>
                {erpMode === 'sync'
                  ? 'Pega los numeros de factura separados por coma para sincronizar en ERP.'
                  : 'Pega los numeros de factura separados por coma para desincronizar del ERP.'}
              </DialogDescription>
              <textarea
                className="w-full min-h-[120px] p-3 border rounded-md text-sm font-mono bg-white text-foreground resize-y focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="A015000465130, FE894, FVE2534066, FVE5, FVE9..."
                value={erpInputText}
                onChange={(e) => setErpInputText(e.target.value)}
              />
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={handleErpSyncClose}>
                  Cancelar
                </Button>
                <Button onClick={handleErpSyncConfirm} disabled={!erpInputText.trim()}>
                  {erpMode === 'sync' ? 'Sincronizar' : 'Desincronizar'}
                </Button>
              </DialogFooter>
            </div>
          )}

          {erpSyncStep === 'syncing' && (
            <div className="flex flex-col items-center py-8 gap-3">
              <RefreshCw className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">
                {erpMode === 'sync' ? 'Sincronizando facturas con ERP...' : 'Desincronizando facturas del ERP...'}
              </p>
            </div>
          )}

          {erpSyncStep === 'done' && (
            <div className="space-y-4">
              <DialogDescription asChild>
                <div className="max-h-72 overflow-y-auto space-y-1.5">
                  {erpSyncResults.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded text-sm">
                      {r.status === 'ok' && <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />}
                      {r.status === 'skipped' && <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" />}
                      {r.status === 'error' && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                      <span className="font-mono">{r.numero}</span>
                      <span className="text-muted-foreground ml-auto">{r.message}</span>
                    </div>
                  ))}
                </div>
              </DialogDescription>
              <DialogFooter>
                <Button onClick={handleErpSyncClose}>Cerrar</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
